/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { createContext, ReactNode, useContext } from 'react';
import api from '../services/api';

import {
  DeliveryMethod,
  Order,
  OrderCancellationReason,
  OrderCreationAttributes,
  OrderStatus,
  PaymentStatus,
} from '../models/Order';

// Define the context type
interface OrderContextType {
  createOrder: (
    orderDetails: OrderCreationAttributes,
    products: { id: number; quantity: number }[],
  ) => Promise<Order>;
  getOrder: (id: string) => Promise<Order>;
  getOrders: () => Promise<Order[]>;
  searchOrders: (
    page?: number,
    limit?: number,
    query?: string,
    paymentStatus?: PaymentStatus,
    deliveryMethod?: DeliveryMethod,
    orderStatus?: OrderStatus,
  ) => Promise<{
    orders: Order[];
    page: number;
    totalPages: number;
    totalOrders: number;
  }>;
  cancelOrder: (
    id: string,
    reason: OrderCancellationReason,
    reasonMesage: string,
  ) => Promise<void>;
  changeOrderStatus: (id: string) => Promise<void>;
  getDeliveryOptions: (productIds: number[]) => Promise<
    {
      deliveryCost: number;
      deliveryMethod: DeliveryMethod;
    }[]
  >;
  setPackageNumber: (id: string, packageNumber: string) => Promise<void>;
  packageSent: (id: string) => Promise<void>;
  finishOrder: (id: string) => Promise<void>;
}

// Create the context
const OrderContext = createContext<OrderContextType | undefined>(undefined);

// Custom hook for using the OrderContext
export const useOrder = () => {
  const context = useContext(OrderContext);
  if (!context) {
    throw new Error('useOrder must be used within an OrderProvider');
  }
  return context;
};

interface OrderProviderProps {
  children: ReactNode;
}

export const OrderProvider: React.FC<OrderProviderProps> = ({ children }) => {
  const reactAppApiUrl = process.env.REACT_APP_API_URL;
  if (!reactAppApiUrl) {
    throw new Error('REACT_APP_API_URL is not set');
  }

  const apiUrl = new URL('orders/', reactAppApiUrl).toString();

  // Create an order
  const createOrder = async (
    orderDetails: OrderCreationAttributes,
    products: { id: number; quantity: number }[],
  ): Promise<Order> => {
    try {
      const response = await api.post(new URL('create', apiUrl).toString(), { 
        orderDetails, 
        products 
      });
      return response.data;
    } catch (error: any) {
      if (error.response) {
        throw new Error(error.response.data.message || 'Błąd serwera');
      }
      throw new Error(error.message || 'Błąd serwera');
    }
  };

  // Fetch a specific order by ID
  const getOrder = async (id: string): Promise<Order> => {
    try {
      const response = await api.get(new URL(id.toString(), apiUrl).toString());
      return response.data.order;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Błąd serwera');
    }
  };

  // Fetch all orders for the current user
  const getOrders = async (): Promise<Order[]> => {
    try {
      const response = await api.get(apiUrl);
      return response.data.orders;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Błąd serwera');
    }
  };

  // Search for orders
  const searchOrders = async (
    page = 1,
    limit = 10,
    query = '',
    paymentStatus?: PaymentStatus,
    deliveryMethod?: DeliveryMethod,
    orderStatus?: OrderStatus,
  ): Promise<{
    orders: Order[];
    page: number;
    totalPages: number;
    totalOrders: number;
  }> => {
    try {
      const response = await api.get(new URL('search', apiUrl).toString(), {
        params: {
          page,
          limit,
          query,
          paymentStatus,
          deliveryMethod,
          orderStatus,
        },
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Błąd serwera');
    }
  };

  // Cancel an order
  const cancelOrder = async (
    id: string,
    reason: OrderCancellationReason,
    reasonMessage: string,
  ): Promise<void> => {
    try {
      await api.put(new URL(`${id}/cancel`, apiUrl).toString(), { 
        reason, 
        reasonMessage 
      });
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Błąd serwera');
    }
  };

  // Change the status of an order
  const changeOrderStatus = async (id: string): Promise<void> => {
    try {
      await api.put(new URL(`${id}/status`, apiUrl).toString());
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Błąd serwera');
    }
  };

  // Get delivery options for a list of products
  const getDeliveryOptions = async (productIds: number[]) => {
    try {
      const response = await api.get(new URL('delivery-options', apiUrl).toString(), {
        params: { productIds },
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Błąd serwera');
    }
  };

  // Set the package number for an order
  const setPackageNumber = async (id: string, packageNumber: string) => {
    try {
      await api.put(new URL(`${id}/package-number`, apiUrl).toString(), {
        packageNumber 
      });
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Błąd serwera');
    }
  };

  // Mark the package as sent
  const packageSent = async (id: string) => {
    try {
      await api.put(new URL(`${id}/sent`, apiUrl).toString());
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Błąd serwera');
    }
  };

  // Finalize the order (admin only)
  const finishOrder = async (id: string): Promise<void> => {
    try {
      await api.put(new URL(`${id}/finish`, apiUrl).toString());
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Błąd serwera');
    }
  };

  return (
    <OrderContext.Provider
      value={{
        createOrder,
        getOrder,
        getOrders,
        searchOrders,
        cancelOrder,
        changeOrderStatus,
        getDeliveryOptions,
        setPackageNumber,
        packageSent,
        finishOrder,
      }}
    >
      {children}
    </OrderContext.Provider>
  );
};
